import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  ListChecks,
  PlusCircle,
  Scale,
  ShieldCheck,
  UserRound,
  Repeat,
  LogOut,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  AnimatedSidebar,
  AnimatedSidebarProvider,
  AnimatedSidebarRail,
  AnimatedSidebarTrigger,
  useAnimatedSidebar,
} from '@/components/motion/animated-sidebar'
import { CommandPalette } from '@/components/motion/command-palette'
import '@/styles/aura-app.css'

export type NavKey =
  | 'dashboard'
  | 'new-interview'
  | 'my-interviews'
  | 'recruiter'
  | 'compare'
  | 'admin'
  | 'profile'

interface AppShellProps {
  /** Nav item highlighted as current. */
  active?: NavKey
  /** In-content page header. Pages that render their own screen-head omit this. */
  title?: string
  /** Optional lede under the title. */
  sub?: string
  /** Optional action buttons on the header row. */
  actions?: ReactNode
  children?: ReactNode
}

const ICONS = {
  dashboard: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,
  plus: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg>,
  list: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 6h13M8 12h13M8 18h13"/><path d="M3 6h.01M3 12h.01M3 18h.01"/></svg>,
  compare: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 20v-8M12 20V5M18 20v-6"/></svg>,
  shield: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l8 3v6c0 4.5-3.2 7.7-8 9-4.8-1.3-8-4.5-8-9V6z"/></svg>,
  user: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  swap: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7h11l-3-3M17 17H6l3 3M18 7l-3 3M6 17l3-3"/></svg>,
  chevron: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>,
  menu: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16"/></svg>,
}

/** Collapse toggle. Lives inside the AnimatedSidebar panel so it can read the
 *  provider context — AppShell itself renders above the provider. */
function CollapseToggle() {
  const { open, toggleSidebar } = useAnimatedSidebar()
  return (
    <button
      type="button"
      className="sidebar-toggle"
      aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
      aria-expanded={open}
      onClick={toggleSidebar}
    >
      {ICONS.chevron}
    </button>
  )
}

/**
 * Workspace shell: beUI animated sidebar + content. Pages render the shell
 * themselves, passing their `active` nav highlight and either a `title`
 * (rendered as an in-content header) or nothing (pages that own their header
 * entirely); `children ?? <Outlet />` also lets it serve as a layout route if
 * pages ever stop owning the shell. There is deliberately no topbar. Nav
 * derives from user.role (roles are exclusive; admins keep recruiter access
 * and get an Admin section). Collapse persists (same localStorage key as the
 * pre-beUI shell); below beUI's 767px breakpoint the sidebar becomes a
 * focus-managed sheet toggled from a fixed corner button.
 */
export function AppShell({ active, title, sub, actions, children }: AppShellProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('aura_sidebar_collapsed') === '1',
  )

  useEffect(() => {
    localStorage.setItem('aura_sidebar_collapsed', collapsed ? '1' : '0')
  }, [collapsed])

  const isAdmin = user?.role === 'admin'
  const isRecruiter = user?.role === 'recruiter' || isAdmin
  const profileTo = isRecruiter ? '/profile/recruiter' : '/profile/candidate'

  // ⌘K palette: the same destinations the sidebar renders, plus the role
  // doorway and sign-out — keyboard-reachable navigation for every surface.
  const commands = [
    ...(isRecruiter ? [
      { id: 'dashboard', label: 'Recruiter dashboard', group: 'Navigate', icon: LayoutDashboard, onSelect: () => navigate('/recruiter') },
      { id: 'compare', label: 'Compare candidates', group: 'Navigate', icon: Scale, onSelect: () => navigate('/recruiter/compare') },
      { id: 'new-invite', label: 'New interview invite', group: 'Navigate', icon: PlusCircle, onSelect: () => navigate('/recruiter/new') },
    ] : [
      { id: 'dashboard', label: 'Dashboard', group: 'Navigate', icon: LayoutDashboard, onSelect: () => navigate('/dashboard') },
      { id: 'practice', label: 'New practice interview', group: 'Navigate', icon: PlusCircle, onSelect: () => navigate('/interview') },
      { id: 'my-interviews', label: 'My interviews', group: 'Navigate', icon: ListChecks, onSelect: () => navigate('/my-interviews') },
    ]),
    ...(isAdmin ? [
      { id: 'admin-sessions', label: 'All sessions', group: 'Admin', icon: ShieldCheck, onSelect: () => navigate('/admin') },
    ] : []),
    { id: 'profile', label: 'Profile', group: 'Account', icon: UserRound, onSelect: () => navigate(profileTo) },
    ...(!isAdmin && user ? [
      { id: 'switch-role', label: 'Switch role', group: 'Account', icon: Repeat, onSelect: () => navigate('/choose-role?switch=1') },
    ] : []),
    { id: 'signout', label: 'Sign out', group: 'Account', icon: LogOut, onSelect: logout },
  ]

  const mainNav = isRecruiter ? (
    <>
      <Link to="/recruiter" className="nav-btn" aria-current={active === 'recruiter' ? 'page' : undefined}>
        {ICONS.dashboard}<span className="nav-label">Dashboard</span>
      </Link>
      <Link to="/recruiter/compare" className="nav-btn" aria-current={active === 'compare' ? 'page' : undefined}>
        {ICONS.compare}<span className="nav-label">Compare</span>
      </Link>
      <Link to="/recruiter/new" className="nav-btn" aria-current={active === 'new-interview' ? 'page' : undefined}>
        {ICONS.plus}<span className="nav-label">New interview</span>
      </Link>
    </>
  ) : (
    <>
      <Link to="/dashboard" className="nav-btn" aria-current={active === 'dashboard' ? 'page' : undefined}>
        {ICONS.dashboard}<span className="nav-label">Dashboard</span>
      </Link>
      <Link to="/interview" className="nav-btn" aria-current={active === 'new-interview' ? 'page' : undefined}>
        {ICONS.plus}<span className="nav-label">New interview</span>
      </Link>
      <Link to="/my-interviews" className="nav-btn" aria-current={active === 'my-interviews' ? 'page' : undefined}>
        {ICONS.list}<span className="nav-label">My interviews</span>
      </Link>
    </>
  )

  return (
    <AnimatedSidebarProvider
      open={!collapsed}
      onOpenChange={nextOpen => setCollapsed(!nextOpen)}
      // The widths stay token-owned: aura-tokens.css keeps --sidebar-w /
      // --sidebar-collapsed-w as the single source of truth.
      style={{ '--sidebar-width': 'var(--sidebar-w)', '--sidebar-width-icon': 'var(--sidebar-collapsed-w)' } as CSSProperties}
      className="app-shell"
    >
      {/* The aside keeps beUI's unnamed "Sidebar" label on purpose: naming it
          "Primary" too would give two landmarks the same name. */}
      <AnimatedSidebar panelClassName="sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-mark" aria-hidden="true">A</span>
          <span className="sidebar-name">Aura</span>
          <CollapseToggle />
        </div>

        <nav className="sidebar-nav" aria-label="Primary">
          <p className="nav-section">Menu</p>
          {mainNav}
          {isAdmin && (
            <>
              <p className="nav-section">Admin</p>
              <Link to="/admin" className="nav-btn" aria-current={active === 'admin' ? 'page' : undefined}>
                {ICONS.shield}<span className="nav-label">All sessions</span>
              </Link>
            </>
          )}
        </nav>

        <div className="sidebar-end">
          {!isAdmin && user && (
            // Server-enforced usage locks decide whether switching is
            // actually allowed; the link is just the doorway to the picker.
            <Link to="/choose-role?switch=1" className="nav-btn">
              {ICONS.swap}<span className="nav-label">Switch role</span>
            </Link>
          )}
          {user?.role === '' && (
            <Link to="/choose-role" className="nav-btn">
              {ICONS.swap}<span className="nav-label">Choose role</span>
            </Link>
          )}
          <Link to={profileTo} className="nav-btn" aria-current={active === 'profile' ? 'page' : undefined}>
            {ICONS.user}<span className="nav-label">Profile</span>
          </Link>
          <button type="button" className="nav-btn" onClick={logout}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 12H4m0 0 4-4m-4 4 4 4"/><path d="M10 4h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-8"/></svg>
            <span className="nav-label">Sign out</span>
          </button>
        </div>

        <AnimatedSidebarRail />
      </AnimatedSidebar>

      <div className="main-col">
        {/* Sheet toggle: fixed corner button, phones only (the desktop rail is
            the AnimatedSidebar above; its trigger would double as a second
            collapse control). The sheet itself — scrim, focus trap, Escape —
            is the provider's portal. */}
        <AnimatedSidebarTrigger
          type="button"
          className="wmenu-btn"
          aria-label="Open menu"
        >
          {ICONS.menu}
        </AnimatedSidebarTrigger>

        <div className="views">
          <main className="wview is-active">
            {title && (
              <header className="wpage-head">
                <div className="wpage-head-text">
                  <h1 className="wpage-head-title">{title}</h1>
                  {sub && <p className="wpage-head-sub">{sub}</p>}
                </div>
                {actions && <div className="wpage-head-actions">{actions}</div>}
              </header>
            )}
            {children ?? <Outlet />}
          </main>
        </div>
      </div>

      <CommandPalette items={commands} placeholder="Where to?" />
    </AnimatedSidebarProvider>
  )
}
